import { formatDate } from "../utils/dates";
import { FormData, FormFieldConfig, PossibleFormFieldValues } from "../interfaces/IForms";
import { newParticipantFormFields } from "../components/users/fieldsLists";
import { useParticipantsContext } from "../contexts/ParticipantsContext";
import { useState } from "react";

interface UseParticipantAddFormReturn {
  // Stan
  formData: Record<string, PossibleFormFieldValues>;
  formErrors: Record<string, string[]>;
  infoMessage: string | null;
  errorMessage: string | null;
  isExpanded: boolean;

  // Akcje
  handleFieldChange: (key: string, value: PossibleFormFieldValues) => void;
  handleFormSubmit: () => Promise<void>;
  toggleExpanded: () => void;
  resetForm: () => void;
  resetFormData: () => void;
}

/**
 * Hook do zarządzania formularzem dodawania zawodnika
 * Zgodny z zasadą pojedynczej odpowiedzialności - tylko logika formularza
 */
export const useParticipantAddForm = (): UseParticipantAddFormReturn => {
  const { handleParticipantAddFromForm } = useParticipantsContext();

  // Stan formularza
  const [formData, setFormData] = useState<Record<string, PossibleFormFieldValues>>(
    newParticipantFormFields.reduce((acc, field) => {
      acc[field.key] = field.value;
      return acc;
    }, {} as FormData)
  );

  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);

  // Walidacja pojedynczego pola
  const validateField = (
    field: FormFieldConfig<PossibleFormFieldValues>,
    value: PossibleFormFieldValues
  ): string[] => {
    if (!field.validators) return [];

    return field.validators
      .map((validator: (value: PossibleFormFieldValues) => string | null) => validator(value))
      .filter((error: string | null): error is string => error !== null && error !== "");
  };

  // Zmiana wartości pola z walidacją
  const handleFieldChange = (key: string, value: PossibleFormFieldValues) => {
    setFormData((prev) => ({ ...prev, [key]: value }));

    const field = newParticipantFormFields.find((f) => f.key === key);
    if (field) {
      const errors = validateField(field, value);
      setFormErrors((prev) => ({ ...prev, [key]: errors }));
    }
  };

  // Walidacja całego formularza
  const validateForm = (): Record<string, string[]> => {
    const errors: Record<string, string[]> = {};

    newParticipantFormFields.forEach((field) => {
      const fieldErrors = validateField(field, formData[field.key]);
      if (fieldErrors.length > 0) {
        errors[field.key] = fieldErrors;
      }
    });

    return errors;
  };

  // Reset formularza (bez komunikatów)
  const resetFormData = () => {
    setFormData(
      newParticipantFormFields.reduce((acc, field) => {
        acc[field.key] = field.value;
        return acc;
      }, {} as FormData)
    );
    setFormErrors({});
  };

  // Reset wszystkiego (z komunikatami)
  const resetForm = () => {
    resetFormData();
    setInfoMessage(null);
    setErrorMessage(null);
  };

  // Wysłanie formularza
  const handleFormSubmit = async () => {
    setInfoMessage(null);
    setErrorMessage(null);

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setErrorMessage("Formularz zawiera błędy.");
      return;
    }

    try {
      const result = await handleParticipantAddFromForm(formData);

      if (result.success && result.participant) {
        setInfoMessage(
          `Dodano zawodnika: ${result.participant.firstName} ${
            result.participant.lastName
          } (${formatDate(result.participant.birthDate)})`
        );
        // Reset tylko danych formularza, zostaw komunikat
        resetFormData();
      } else {
        setErrorMessage(
          result.error || "Wystąpił błąd podczas dodawania zawodnika. Spróbuj ponownie."
        );
      }
    } catch (error) {
      setErrorMessage("Wystąpił nieoczekiwany błąd. Spróbuj ponownie.");
    }
  };

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  return {
    // Stan
    formData,
    formErrors,
    infoMessage,
    errorMessage,
    isExpanded,

    // Akcje
    handleFieldChange,
    handleFormSubmit,
    toggleExpanded,
    resetForm,
    resetFormData,
  };
};
